import { useEffect } from "react";
import JoplinHeroSection from "./components/HeroSection";
import JoplinStickyNav from "./components/StickyNav";
import JoplinProcessingTimeSection from "./components/ProcessingTimeSection";
import JoplinServicesSection from "./components/ServicesSection";
import JoplinLocalAreaSection from "./components/LocalAreaSection";
import JoplinTravelTimingSection from "./components/TravelTimingSection";
import JoplinDestinationsSection from "./components/DestinationsSection";
import JoplinTestimonialsSection from "./components/TestimonialsSection";
import JoplinAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import JoplinComparisonSection from "./components/ComparisonSection";
import JoplinMistakesSection from "./components/MistakesSection";
import JoplinHowItWorksSection from "./components/HowItWorksSection";
import JoplinFAQSection, { faqs } from "./components/FAQSection";
import JoplinHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import JoplinFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function JoplinPage() {
  useEffect(() => {
    document.title = "Emergency Passport Joplin, MO — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/joplin/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Joplin, MO — as fast as 24 hours. No 3-hour drive to St. Louis required. A+ BBB rated, registered State Dept. courier serving Tri-State area business travelers, MSSU students, agriculture industry professionals, and families in Jasper and Newton counties.");
    setMeta("geo.placename", "Joplin, Missouri");
    setMeta("geo.region", "US-MO");
    setMeta("geo.position", "37.0842;-94.5133");
    setMeta("ICBM", "37.0842, -94.5133");

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Missouri", item: "https://fastpassportcenter.com/missouri/" },
          { "@type": "ListItem", position: 3, name: "Joplin Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center provides emergency and expedited passport services for Joplin and the Tri-State region. As fast as 24 hours — no drive to St. Louis required.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: { "@type": "City", name: "Joplin", containedInPlace: { "@type": "State", name: "Missouri" } },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data-joplin";
    document.getElementById("structured-data-joplin")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      document.getElementById("structured-data-joplin")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <JoplinHeroSection />
      <JoplinStickyNav />
      <JoplinProcessingTimeSection />
      <JoplinServicesSection />
      <JoplinLocalAreaSection />
      <JoplinTravelTimingSection />
      <JoplinDestinationsSection />
      <JoplinTestimonialsSection />
      <JoplinAcceptanceFacilitiesSection />
      <JoplinComparisonSection />
      <JoplinMistakesSection />
      <JoplinHowItWorksSection />
      <JoplinFAQSection />
      <JoplinHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/joplin" state="MO" />
      <JoplinFinalCTA />
    </div>
  );
}